import type { Article } from '../types/content';

/*
 * Editorial content. All entries below are SAMPLE editorial content (isSample: true)
 * until approved articles are published. Set `author` and `publishedAt` only when confirmed.
 * When connected to a CMS, replace this array with fetched records of the same shape.
 */
export const insightCategories = [
  'Executive Recruitment',
  'Leadership Strategy',
  'Board Governance',
  'Senior Talent Trends',
  'Executive Succession',
  'Organizational Transformation',
  'Industry Developments',
  'Talent Market Intelligence',
];

export const articles: Article[] = [
  {
    slug: 'hiring-your-next-executive',
    title: 'What Organizations Should Consider When Hiring Their Next Executive',
    category: 'Executive Recruitment',
    excerpt:
      'Before defining the candidate, define the moment. The most effective executive appointments begin with clarity about what the organization needs next.',
    readingTime: '6 min read',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/80e77fd1-70af-453e-8a6b-8dff63439e89.jpg',
    imageAlt: 'An empty boardroom with a long walnut table in warm afternoon light',
    isSample: true,
    author: null,
    publishedAt: null,
    sections: [
      {
        heading: 'Start with the moment, not the job description',
        paragraphs: [
          'Executive roles rarely exist in isolation. A new chief financial officer may be needed to prepare for investment; a new chief operating officer may be needed because the business has outgrown its founding structure. The same title can describe very different jobs.',
          'Before writing a specification, leadership teams benefit from agreeing what the organization needs to achieve in the next three to five years, and what this role must contribute to that.',
        ],
      },
      {
        heading: 'Separate essential experience from familiar experience',
        paragraphs: [
          'It is natural to look for someone who has done the role before in a similar organization. Sometimes that is essential. Often, however, the qualities that matter most — judgment, the ability to lead through ambiguity, credibility with a board — can be found in leaders whose paths look different.',
          'Being explicit about which requirements are genuinely non-negotiable widens the field without lowering the bar.',
        ],
      },
      {
        heading: 'Align stakeholders early',
        paragraphs: [
          'Executive searches lose momentum when stakeholders hold different views of the ideal candidate. A short alignment conversation at the outset — covering priorities, decision-making, and timeline — prevents difficult surprises at the shortlist stage.',
        ],
      },
      {
        heading: 'Consider the first year, not just the appointment',
        paragraphs: [
          'An appointment is the start of a relationship. Thinking ahead about onboarding, early priorities, and how success will be measured helps both the organization and the new leader begin well.',
        ],
      },
    ],
  },
  {
    slug: 'modern-board-leadership',
    title: 'The Changing Expectations of Modern Board Leadership',
    category: 'Board Governance',
    excerpt:
      'Boards are being asked to oversee a broader range of issues than ever. That has implications for how directors are selected and how boards are composed.',
    readingTime: '5 min read',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e3b0b2b9-0c1d-49b6-9b99-67fd3f6be580.jpg',
    imageAlt: 'A sculptural limestone staircase with a dark steel balustrade',
    isSample: true,
    author: null,
    publishedAt: null,
    sections: [
      {
        heading: 'A broader agenda',
        paragraphs: [
          'Alongside financial performance and strategy, boards increasingly engage with technology risk, organizational culture, stakeholder expectations, and long-term resilience. Few individual directors can cover all of these, which makes composition a collective question.',
        ],
      },
      {
        heading: 'From individual credentials to collective capability',
        paragraphs: [
          'A useful starting point for any board appointment is a candid review of the skills and perspectives the board already has — and those it lacks. Each new director can then be selected to strengthen the whole rather than to replicate existing strengths.',
        ],
      },
      {
        heading: 'Independence and engagement',
        paragraphs: [
          'Effective non-executive directors combine independence of mind with genuine engagement. They ask difficult questions constructively, invest time in understanding the business, and support management without becoming part of it.',
          'Assessing these qualities requires conversation and reference work that goes beyond a CV.',
        ],
      },
    ],
  },
  {
    slug: 'leadership-teams-long-term-growth',
    title: 'Building Leadership Teams for Long-Term Growth',
    category: 'Leadership Strategy',
    excerpt:
      'Strong individual leaders do not automatically form strong leadership teams. Growth asks organizations to think about how their leaders work together.',
    readingTime: '5 min read',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/11125ac0-8437-416e-9b5f-0e923ea077bc.jpg',
    imageAlt: 'Stacked limestone and glass building volumes in warm evening light',
    isSample: true,
    author: null,
    publishedAt: null,
    sections: [
      {
        heading: 'Teams, not collections of individuals',
        paragraphs: [
          'Each appointment changes the dynamics of a leadership team. When hiring, it helps to ask not only whether a candidate is excellent, but how they will complement the strengths, styles, and gaps of the people around them.',
        ],
      },
      {
        heading: 'Hiring ahead of the curve',
        paragraphs: [
          'Organizations in growth often appoint leaders for the business they are today. Considering the scale and complexity expected in a few years — and selecting leaders who have operated at that level — reduces the need for repeated changes.',
        ],
      },
      {
        heading: 'Sequencing appointments',
        paragraphs: [
          'When several senior roles need filling, the order matters. Appointing a chief executive or functional head first often allows them to shape the team that follows, improving alignment and ownership.',
        ],
      },
    ],
  },
  {
    slug: 'executive-succession-continuous-discipline',
    title: 'Approaching Executive Succession as a Continuous Discipline',
    category: 'Executive Succession',
    excerpt:
      'Succession planning is most effective when it is treated as an ongoing conversation rather than an exercise triggered by a departure.',
    readingTime: '4 min read',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/bac7718a-bd62-4c65-ba9a-2ad3974094e0.jpg',
    imageAlt: 'Two armchairs facing each other beside a large window',
    isSample: true,
    author: null,
    publishedAt: null,
    sections: [
      {
        heading: 'Identify the roles that matter most',
        paragraphs: [
          'Not every role carries the same succession risk. Starting with the positions whose sudden vacancy would most disrupt strategy or operations focuses effort where it counts.',
        ],
      },
      {
        heading: 'Look honestly at readiness',
        paragraphs: [
          'Internal successors may be ready now, ready with development, or not yet the right fit. Distinguishing between these — and benchmarking against the external market — gives boards a clearer view of their options.',
        ],
      },
      {
        heading: 'Revisit regularly',
        paragraphs: [
          'Strategies change, people develop, and markets move. A succession plan reviewed periodically remains useful; one written once and filed away rarely is.',
        ],
      },
    ],
  },
  {
    slug: 'what-transformation-asks-of-leaders',
    title: 'What Transformation Asks of Senior Leaders',
    category: 'Organizational Transformation',
    excerpt:
      'Transformation programmes succeed or stall on leadership. The qualities required to lead change are not always the same as those required to run a stable business.',
    readingTime: '5 min read',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e516ed98-403a-4804-b816-d5dd997b31ee.jpg',
    imageAlt: 'A modern facade with repeating copper fins in golden-hour light',
    isSample: true,
    author: null,
    publishedAt: null,
    sections: [
      {
        heading: 'Different moments, different leaders',
        paragraphs: [
          'Leading a transformation demands comfort with ambiguity, the ability to build coalitions, and the resilience to sustain momentum when early results are uneven. These qualities can be assessed deliberately during a search.',
        ],
      },
      {
        heading: 'Credibility with the existing organization',
        paragraphs: [
          'Change leaders must respect what already works while making the case for what must change. Candidates who can show how they have earned trust in established organizations are often more effective than those who arrive with a fixed playbook.',
        ],
      },
      {
        heading: 'Clarity of mandate',
        paragraphs: [
          'A transformation leader needs a clear mandate and visible support from the board and chief executive. Agreeing that mandate before the search begins helps attract candidates who will thrive in the role.',
        ],
      },
    ],
  },
  {
    slug: 'market-intelligence-before-a-search',
    title: 'Why Market Intelligence Belongs at the Start of a Search',
    category: 'Talent Market Intelligence',
    excerpt:
      'Understanding the leadership landscape before launching a search helps organizations set realistic expectations and make better decisions.',
    readingTime: '4 min read',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6b194036-a307-45c2-91d7-5e2c23cc0b5e.jpg',
    imageAlt: 'A desk with an open notebook, a fountain pen, and a folded paper map',
    isSample: true,
    author: null,
    publishedAt: null,
    sections: [
      {
        heading: 'Testing the brief against reality',
        paragraphs: [
          'A role specification describes an ideal. Early market research shows how many leaders genuinely match it, where they are, and what might motivate them to consider a move — allowing the brief to be refined before time is invested.',
        ],
      },
      {
        heading: 'Seeing how others are organized',
        paragraphs: [
          'Understanding how comparable organizations structure their leadership teams can reveal alternative role designs, reporting lines, or combinations of responsibility worth considering.',
        ],
      },
      {
        heading: 'Informing more than one hire',
        paragraphs: [
          'Insight gathered for one search often informs succession planning, future appointments, and wider leadership strategy, making it a durable investment.',
        ],
      },
    ],
  },
];
